import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { scrollTo } from "@/components/Nav";
import { COMPANY, CORPORATE_PANTRY_FORM_URL } from "@/data/site";

const linkClass =
  "group inline-flex items-center gap-1 hover:text-[#C07C5F] transition-colors duration-500";

const Arrow = () => (
  <ArrowUpRight size={12} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
);

const COLUMNS = [
  {
    heading: "Help",
    links: [
      { label: "Contact Us", to: "/contact" },
      { label: "FAQ", section: "#faq" },
      { label: "Terms of Service", to: "/terms-of-service" },
      { label: "My Account", to: "/account" },
      { label: "My Loyalty Points", to: "/loyalty" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Our Policies", to: "/our-policies" },
    ],
  },
  {
    heading: "Shop",
    links: [
      { label: "The Collection", section: "#collection" },
      { label: "Build Your Box", section: "#build-box" },
      { label: "Protein Index", section: "#calculator" },
      { label: "The Formula", section: "#formula" },
    ],
  },
  {
    heading: "DNA",
    links: [
      { label: "Manifesto", section: "#manifesto" },
      { label: "The Family", section: "#reviews" },
      CORPORATE_PANTRY_FORM_URL
        ? { label: "Corporate Pantry", href: CORPORATE_PANTRY_FORM_URL }
        : { label: "Corporate Pantry", to: "/contact" },
    ],
  },
];

const Footer = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const goToSection = (section) => {
    if (pathname === "/") return scrollTo(section);
    navigate("/");
    setTimeout(() => scrollTo(section), 400);
  };

  const renderLink = (link) => {
    const tid = `footer-link-${link.label.toLowerCase().replace(/[^a-z]+/g, "-")}`;
    if (link.href) {
      return (
        <a href={link.href} target="_blank" rel="noreferrer" data-testid={tid} className={linkClass}>
          {link.label}
          <Arrow />
        </a>
      );
    }
    if (link.section) {
      return (
        <button onClick={() => goToSection(link.section)} data-testid={tid} className={linkClass}>
          {link.label}
          <Arrow />
        </button>
      );
    }
    return (
      <Link to={link.to} data-testid={tid} className={linkClass}>
        {link.label}
        <Arrow />
      </Link>
    );
  };

  return (
    <footer className="relative bg-[#111111] text-[#F9F8F6] overflow-hidden grain" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-12">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="text-[10px] tracking-[0.5em] uppercase text-[#C07C5F] mb-8"
        >
          Now Shipping Across India — Order Now
        </motion.p>

        <div className="overflow-hidden">
          <motion.img
            src="/assets/dna-logo.png?v=6"
            alt="DNA — Daily Nutrition Aesthetics"
            initial={{ y: "100%" }}
            whileInView={{ y: "0%" }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-[1100px]"
            data-testid="footer-wordmark"
          />
        </div>

        <div className="mt-16 md:mt-24 grid grid-cols-2 md:grid-cols-12 gap-12 border-t border-white/10 pt-14" data-testid="footer-columns">
          <div className="col-span-2 md:col-span-3">
            <p className="text-[11px] tracking-[0.3em] uppercase text-white/50 mb-6">Daily Nutrition Aesthetics</p>
            <a href={`mailto:${COMPANY.email}`} className="font-serif-lux text-2xl text-white/90 hover:text-[#C07C5F] transition-colors duration-500">
              {COMPANY.email}
            </a>
            {COMPANY.phone && <p className="font-serif-lux text-xl text-white/70 mt-2">{COMPANY.phone}</p>}
            <p className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-3">{COMPANY.hours}</p>
          </div>

          {COLUMNS.map((col) => (
            <div key={col.heading} className="md:col-span-3">
              <p className="text-[10px] tracking-[0.4em] uppercase text-[#C07C5F] mb-6">{col.heading}</p>
              <ul className="flex flex-col gap-4 text-[11px] tracking-[0.25em] uppercase text-white/60">
                {col.links.map((link) => (
                  <li key={link.label}>{renderLink(link)}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div
          className="mt-14 border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between gap-4 text-[10px] tracking-[0.2em] uppercase text-white/40 leading-relaxed"
          data-testid="footer-company-info"
        >
          <p>
            <span className="text-white/60">{COMPANY.legalName}</span>
            <br />
            CIN: {COMPANY.cin}
          </p>
          <p className="md:text-center">© 2026 DNA. All rights reserved.</p>
          <p className="md:text-right md:max-w-md">
            {COMPANY.address[0]}
            <br />
            {COMPANY.address[1]}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
